import * as React from "react"
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "react-feather"
import { type ButtonProps, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn("flex flex-row items-center gap-2", className)} {...props} />
  )
)
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn("", className)} {...props} />
)
PaginationItem.displayName = "PaginationItem"

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">

const PaginationLink = ({ className, isActive, size = "icon", ...props }: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size,
      }),
      className
    )}
    {...props}
  />
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { disabled?: boolean }) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn(
      "h-8 w-8 rounded-[10px] border border-slate-200 bg-white p-0 hover:bg-slate-50",
      disabled && "pointer-events-none opacity-50",
      className
    )}
    {...props}
  >
    <ChevronLeft
      className={cn("h-[18px] w-[18px]", disabled ? "text-slate-400" : "text-slate-700")}
    />
  </PaginationLink>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { disabled?: boolean }) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn(
      "h-8 w-8 rounded-[10px] border border-slate-200 bg-white p-0 hover:bg-slate-50",
      disabled && "pointer-events-none opacity-50",
      className
    )}
    {...props}
  >
    <ChevronRight
      className={cn("h-[18px] w-[18px]", disabled ? "text-slate-400" : "text-slate-700")}
    />
  </PaginationLink>
)
PaginationNext.displayName = "PaginationNext"

const PaginationFirst = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { disabled?: boolean }) => (
  <PaginationLink
    aria-label="Go to first page"
    className={cn(
      "h-8 w-8 rounded-[10px] border border-slate-200 bg-white p-0 hover:bg-slate-50",
      disabled && "pointer-events-none opacity-50",
      className
    )}
    {...props}
  >
    <ChevronsLeft
      className={cn("h-[18px] w-[18px]", disabled ? "text-slate-400" : "text-slate-700")}
    />
  </PaginationLink>
)
PaginationFirst.displayName = "PaginationFirst"

const PaginationLast = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { disabled?: boolean }) => (
  <PaginationLink
    aria-label="Go to last page"
    className={cn(
      "h-8 w-8 rounded-[10px] border border-slate-200 bg-white p-0 hover:bg-slate-50",
      disabled && "pointer-events-none opacity-50",
      className
    )}
    {...props}
  >
    <ChevronsRight
      className={cn("h-[18px] w-[18px]", disabled ? "text-slate-400" : "text-slate-700")}
    />
  </PaginationLink>
)
PaginationLast.displayName = "PaginationLast"

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
